// Web Audio API Sound Synthesizer for Lucky 7
// Synthesizes tactile, rolling, landing, victory fanfare, and countdown sounds.

class Lucky7AudioEngine {
  private ctx: AudioContext | null = null;
  private enabled = true;
  private activeSources: (OscillatorNode | AudioBufferSourceNode)[] = [];
  private noiseBuffer: AudioBuffer | null = null;

  init() {
    if (typeof window === 'undefined') return;
    if (!this.ctx) {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AC) {
        this.ctx = new AC();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
  }

  toggle(enabled: boolean) {
    this.enabled = enabled;
    if (!enabled) {
      this.stopRollingSound();
    }
  }

  // Helper to generate a cached white noise buffer for realistic shuffling sound
  private getNoiseBuffer(): AudioBuffer | null {
    if (typeof window === 'undefined' || !this.ctx) return null;
    if (!this.noiseBuffer) {
      const bufferSize = this.ctx.sampleRate * 2; // 2 seconds of noise
      const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }
      this.noiseBuffer = buffer;
    }
    return this.noiseBuffer;
  }

  // 1. Tactile UI Tap Click Feedback
  playTap() {
    if (!this.enabled) return;
    this.init();
    const c = this.ctx;
    if (!c) return;

    const t0 = c.currentTime;
    const osc = c.createOscillator();
    const gain = c.createGain();

    osc.type = 'square';
    osc.frequency.setValueAtTime(1200, t0);

    gain.gain.setValueAtTime(0.06, t0);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.05);

    osc.connect(gain).connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + 0.06);
  }

  // 2. Countdown Timer Urgency Tick
  playTimerTick() {
    if (!this.enabled) return;
    this.init();
    const c = this.ctx;
    if (!c) return;

    const t0 = c.currentTime;
    const osc = c.createOscillator();
    const gain = c.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(500, t0);

    gain.gain.setValueAtTime(0.08, t0);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.04);

    osc.connect(gain).connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + 0.05);
  }

  // Helper to trigger a thick, high-fidelity physical impact sound of a single die hitting the table.
  // Blends multiple detuned oscillators and filtered noise to create a realistic acoustic response.
  private triggerImpact(t: number, boardVol: number, clickVol: number, pitchOffset: number) {
    if (!this.enabled || !this.ctx) return;
    const c = this.ctx;

    // A. Warm Low-Mid Felt/Wood Table Thud (Blended Detuned Triangles + Low Bandpass Noise)
    if (boardVol > 0) {
      // Sub-harmonic oscillator 1 (110Hz body thump)
      const boardOsc1 = c.createOscillator();
      const boardGain1 = c.createGain();
      boardOsc1.type = 'triangle';
      boardOsc1.frequency.setValueAtTime(110 + pitchOffset, t);
      boardOsc1.frequency.exponentialRampToValueAtTime(45, t + 0.05);
      boardGain1.gain.setValueAtTime(boardVol * 0.75, t);
      boardGain1.gain.exponentialRampToValueAtTime(0.001, t + 0.06);
      boardOsc1.connect(boardGain1).connect(c.destination);
      boardOsc1.start(t);
      boardOsc1.stop(t + 0.08);
      this.activeSources.push(boardOsc1);

      // Sub-harmonic oscillator 2 (170Hz board resonance)
      const boardOsc2 = c.createOscillator();
      const boardGain2 = c.createGain();
      boardOsc2.type = 'triangle';
      boardOsc2.frequency.setValueAtTime(170 + pitchOffset * 1.2, t);
      boardOsc2.frequency.exponentialRampToValueAtTime(60, t + 0.05);
      boardGain2.gain.setValueAtTime(boardVol * 0.45, t);
      boardGain2.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
      boardOsc2.connect(boardGain2).connect(c.destination);
      boardOsc2.start(t);
      boardOsc2.stop(t + 0.07);
      this.activeSources.push(boardOsc2);

      // Low-frequency friction noise burst (felt texture)
      const noiseBuf = this.getNoiseBuffer();
      if (noiseBuf) {
        const noiseNode = c.createBufferSource();
        noiseNode.buffer = noiseBuf;
        const filter = c.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.setValueAtTime(150, t);
        filter.Q.setValueAtTime(1.8, t);
        const noiseGain = c.createGain();
        noiseGain.gain.setValueAtTime(boardVol * 0.35, t);
        noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
        noiseNode.connect(filter).connect(noiseGain).connect(c.destination);
        noiseNode.start(t);
        noiseNode.stop(t + 0.06);
        this.activeSources.push(noiseNode);
      }
    }

    // B. Sharp Plastic Dice Collision Click (Detuned Sines + Highpass Noise Burst)
    if (clickVol > 0) {
      // Plastic Clack 1 (2200Hz)
      const clickOsc1 = c.createOscillator();
      const clickGain1 = c.createGain();
      clickOsc1.type = 'sine';
      clickOsc1.frequency.setValueAtTime(2200 + pitchOffset * 4, t);
      clickGain1.gain.setValueAtTime(clickVol * 0.65, t);
      clickGain1.gain.exponentialRampToValueAtTime(0.001, t + 0.015);
      clickOsc1.connect(clickGain1).connect(c.destination);
      clickOsc1.start(t);
      clickOsc1.stop(t + 0.02);
      this.activeSources.push(clickOsc1);

      // Plastic Clack 2 (2900Hz)
      const clickOsc2 = c.createOscillator();
      const clickGain2 = c.createGain();
      clickOsc2.type = 'sine';
      clickOsc2.frequency.setValueAtTime(2900 + pitchOffset * 5, t);
      clickGain2.gain.setValueAtTime(clickVol * 0.45, t);
      clickGain2.gain.exponentialRampToValueAtTime(0.001, t + 0.012);
      clickOsc2.connect(clickGain2).connect(c.destination);
      clickOsc2.start(t);
      clickOsc2.stop(t + 0.02);
      this.activeSources.push(clickOsc2);

      // High-frequency impact noise transient
      const noiseBuf = this.getNoiseBuffer();
      if (noiseBuf) {
        const noiseNode = c.createBufferSource();
        noiseNode.buffer = noiseBuf;
        const filter = c.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.setValueAtTime(4500, t);
        const noiseGain = c.createGain();
        noiseGain.gain.setValueAtTime(clickVol * 0.3, t);
        noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.01);
        noiseNode.connect(filter).connect(noiseGain).connect(c.destination);
        noiseNode.start(t);
        noiseNode.stop(t + 0.015);
        this.activeSources.push(noiseNode);
      }
    }
  }

  // Helper to trigger a pleasant, warm, chime-like melodic note.
  // Combines a triangle wave (warm body) and a sine wave (clean resonance) with a plucky envelope.
  private triggerMelodicNote(t: number, freq: number, vol: number) {
    if (!this.enabled || !this.ctx) return;
    const c = this.ctx;

    const decay = 0.12; // Smooth pleasant decay time

    // Voice 1: Triangle Wave (for a warm wood/chime tone)
    const osc1 = c.createOscillator();
    const gain1 = c.createGain();
    osc1.type = 'triangle';
    osc1.frequency.setValueAtTime(freq, t);
    
    gain1.gain.setValueAtTime(0.0001, t);
    gain1.gain.linearRampToValueAtTime(vol * 0.7, t + 0.005);
    gain1.gain.exponentialRampToValueAtTime(0.001, t + decay);

    osc1.connect(gain1).connect(c.destination);
    osc1.start(t);
    osc1.stop(t + decay + 0.02);
    this.activeSources.push(osc1);

    // Voice 2: Sine Wave (clean resonance)
    const osc2 = c.createOscillator();
    const gain2 = c.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(freq, t);

    gain2.gain.setValueAtTime(0.0001, t);
    gain2.gain.linearRampToValueAtTime(vol * 0.3, t + 0.005);
    gain2.gain.exponentialRampToValueAtTime(0.001, t + decay * 0.8);

    osc2.connect(gain2).connect(c.destination);
    osc2.start(t);
    osc2.stop(t + decay + 0.02);
    this.activeSources.push(osc2);
  }

  // 3. Dice Rolling Tumbler Sound
  // Plays a pleasant, accelerating melodic pentatonic arpeggio that climbs in pitch to build positive excitement.
  // The scale sequence starts slow (160ms interval) and speeds up (80ms interval) near the end,
  // cutting off cleanly at 2.30s to create a dramatic silent cliffhanger just before
  // the physical dice landing thuds at 2.44s and 2.52s.
  startRollingSound() {
    if (!this.enabled) return;
    this.init();
    const c = this.ctx;
    if (!c) return;

    this.stopRollingSound();

    const startTime = c.currentTime;
    const cutoffTime = 2.30; // Melodic roll duration in seconds

    // G Major Pentatonic scale notes for a bright, pleasant cascade
    const pentatonicScale = [
      392.00,  // G4
      440.00,  // A4
      493.88,  // B4
      587.33,  // D5
      659.25,  // E5
      783.99,  // G5
      880.00,  // A5
      987.77,  // B5
      1174.66, // D6
      1318.51, // E6
      1567.98  // G6
    ];

    let currentOffset = 0.0;
    let hitIndex = 0;

    while (currentOffset < cutoffTime) {
      const progress = currentOffset / cutoffTime; // 0.0 to 1.0
      // Accelerate: start slow at 160ms (0.16) and decrease to 80ms (0.08) for a natural crescendo-tempo build-up
      const interval = 0.16 - (progress * 0.08);
      
      const hitTime = startTime + currentOffset;
      // Volume: soft, pleasant, slightly louder near the end
      const noteVol = 0.045 + (progress * 0.065);

      // Select frequency from scale (climbing up, looping around)
      const freq = pentatonicScale[hitIndex % pentatonicScale.length];

      this.triggerMelodicNote(hitTime, freq, noteVol);
      
      currentOffset += interval;
      hitIndex++;
    }

    // B. Physical Dice Lands & Settles (Synchronized with the GIF settling frames)
    // Left Die (Die 1) Lands at 2.44s
    this.triggerImpact(startTime + 2.44, 0.24, 0.055, -20);
    // Right Die (Die 2) Lands at 2.52s
    this.triggerImpact(startTime + 2.52, 0.22, 0.05, 15);
  }

  stopRollingSound() {
    if (this.activeSources.length > 0) {
      this.activeSources.forEach(src => {
        try {
          src.stop();
        } catch (e) {
          // ignore if node was already naturally stopped
        }
      });
      this.activeSources = [];
    }
  }

  // 4. Dice Land (No-Op as timing is pre-scheduled inside startRollingSound for exact visual sync)
  playDiceLand() {
    // Empty to prevent duplicate double thuds during result transitions
  }

  // 5. Win Fanfare Chord & Coin Cascade Rain
  playWinMelody() {
    if (!this.enabled) return;
    this.init();
    const c = this.ctx;
    if (!c) return;

    const t0 = c.currentTime;

    // Arpeggiated Triumphant G Major Chord
    const notes = [
      392.00, // G4
      493.88, // B4
      587.33, // D5
      783.99, // G5
    ];

    notes.forEach((freq, i) => {
      const t = t0 + i * 0.10;
      const osc = c.createOscillator();
      const gain = c.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, t);

      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.linearRampToValueAtTime(0.18, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);

      osc.connect(gain).connect(c.destination);
      osc.start(t);
      osc.stop(t + 0.50);
    });

    // Cascading Coin Shimmers / Rain
    const totalCoins = 18;
    for (let i = 0; i < totalCoins; i++) {
      const t = t0 + 0.35 + Math.random() * 1.2;
      const osc = c.createOscillator();
      const gain = c.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(1800 + Math.random() * 1000, t);

      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.linearRampToValueAtTime(0.08, t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.12);

      osc.connect(gain).connect(c.destination);
      osc.start(t);
      osc.stop(t + 0.15);
    }
  }

  // 6. Loss Descending Minor Melody
  playLossMelody() {
    if (!this.enabled) return;
    this.init();
    const c = this.ctx;
    if (!c) return;

    const t0 = c.currentTime;

    // Descending Minor Chord Progression
    const notes = [
      523.25, // C5
      415.30, // Ab4
      349.23, // F4
      277.18, // Db4
    ];

    notes.forEach((freq, i) => {
      const t = t0 + i * 0.15;
      const osc = c.createOscillator();
      const gain = c.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);

      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.linearRampToValueAtTime(0.12, t + 0.025);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);

      osc.connect(gain).connect(c.destination);
      osc.start(t);
      osc.stop(t + 0.50);
    });
  }
}

export const lucky7AudioEngine = new Lucky7AudioEngine();
